import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import Alert from "../layout/Alert";
import ResponsiveContentWrapper from "../layout/ResponsiveContentWrapper";
import env from "../../env";
import UserInfoWrapper from "../Common/user/UserInfoWrapper";
import { generateSecondaryOtp, validateSecondaryOtp } from "../../action/otp";

const Profile = ({ wrapperClass }) => {
  const [fetchedUser, setFetchedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [openVerifyModal, setOpenVerifyModal] = useState(false);
  const [emailToVerify, setEmailToVerify] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpMessage, setOtpMessage] = useState("");

  useEffect(() => {
    const fetchTokenDetails = async () => {
      try {
        setLoading(true);
        const umURL = env.GET_USERMANAGEMENT_API_URL();
        const token = localStorage.getItem("token") || null;
        const config = {
          headers: {
            "Content-Type": "application/json",
            // ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        };
        const res = await axios.get(`${umURL}/simplifi/v1/userManagement/tokenDetails`, config);
        setFetchedUser(res.data);
      } catch (err) {
        setError(err.response?.data?.message || err.message || "Failed to fetch user");
      } finally {
        setLoading(false);
      }
    };
    fetchTokenDetails();
  }, []);

  const formatUsernameToName = (username) => {
    if (!username) return "";
    const parts = username.split(/[._\s]+/).filter(Boolean);
    return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(" ").trim();
  };

  const formatNameParts = (firstName, lastName) => {
    const f = firstName ? firstName.trim() : "";
    const l = lastName ? lastName.trim() : "";
    const parts = [];
    if (f) parts.push(f.charAt(0).toUpperCase() + f.slice(1).toLowerCase());
    if (l) parts.push(l.charAt(0).toUpperCase() + l.slice(1).toLowerCase());
    return parts.join(" ").trim();
  };

  const extractErrorMessage = (err) => {
    if (!err) return "Unknown error";
    if (typeof err === "string") return err;
    if (err.response && err.response.data) {
      const d = err.response.data;
      if (typeof d === "string") return d;
      if (d.message) return d.message;
      if (d.error) return d.error;
      try {
        return JSON.stringify(d);
      } catch (e) {
        return String(d);
      }
    }
    return err.message || String(err);
  };

  // Send OTP
  const handleSendOtp = async () => {
    if (!emailToVerify) return setOtpMessage("Please enter a valid email");
    try {
      setOtpLoading(true);
      setOtpMessage("");
      await generateSecondaryOtp(emailToVerify);
      setOtpSent(true);
      setOtpMessage("OTP sent to your email successfully.");
    } catch (err) {
      setOtpMessage(extractErrorMessage(err));
    } finally {
      setOtpLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    if (!otp) return setOtpMessage("Please enter the OTP");
    try {
      setOtpLoading(true);
      setOtpMessage("");
      await validateSecondaryOtp(emailToVerify, otp);
      setOtpMessage("Email verified successfully!");
      setOtpSent(false);
      setTimeout(() => setOpenVerifyModal(false), 1500);
    } catch (err) {
      setOtpMessage(extractErrorMessage(err));
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <ResponsiveContentWrapper className={wrapperClass || ""}>
      <div className="content-wrapper">
        <div className="content-block bottom-shadow" style={{ maxWidth: 900 }}>
          <div className="p-15">
            <div className="hd-style-2">
              <h5>User Information</h5>
            </div>

            {loading ? (
              <div style={{ padding: 20 }}>
                <CircularProgress />
              </div>
            ) : error ? (
              <div className="is-danger">{error}</div>
            ) : fetchedUser ? (
              <div className="user-information" style={{ marginTop: 16 }}>
                <Alert />

                {/* Name */}
                <div className="row mb-0">
                  <div className="s4 col">
                    <div className="d-flex info-box">
                      <div className="info">
                        <label className="label">Name</label>
                        <div className="user-about medium">
                          {fetchedUser.username
                            ? formatUsernameToName(fetchedUser.username)
                            : formatNameParts(fetchedUser.firstName, fetchedUser.lastName)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Email + Verify link */}
                  <div className="s4 col">
                    <div className="d-flex info-box">
                      <div className="info">
                        <label className="label">
                          Email ID
                          <span
                            onClick={() => {
                              setEmailToVerify(fetchedUser.email);
                              setOpenVerifyModal(true);
                            }}
                            style={{
                              color: "#0d47a1",
                              cursor: "pointer",
                              marginLeft: 8,
                              fontWeight: 400,
                              textDecoration: "underline",
                              fontSize: "9px",
                            }}
                          >
                            Verify
                          </span>
                        </label>
                        <div className="user-about medium">{fetchedUser.email}</div>
                      </div>
                    </div>
                  </div>

                  {/* Mobile */}
                  <div className="s4 col">
                    <div className="d-flex info-box">
                      <div className="info">
                        <label className="label">Mobile</label>
                        <div className="user-about medium">{fetchedUser.mobile || "-"}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <UserInfoWrapper>
                {({ user, withoutOnboard }) => (
                  <div className="user-information">
                    {user ? (
                      <div className="row mb-0">
                        <div className="s4 col">
                          <div className="d-flex info-box">
                            <div className="info">
                              <label className="label">Name</label>
                              <div className="user-about medium">
                                {user.username
                                  ? formatUsernameToName(user.username)
                                  : formatNameParts(user.firstName, user.lastName)}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="s4 col">
                          <div className="d-flex info-box">
                            <div className="info">
                              <label className="label">
                                Email ID
                                <span
                                  onClick={() => {
                                    setEmailToVerify(user.email);
                                    setOpenVerifyModal(true);
                                  }}
                                  style={{
                                    color: "#1976d2",
                                    cursor: "pointer",
                                    marginLeft: 8,
                                    fontWeight: 400,
                                    textDecoration: "underline",
                                    fontSize: "5px",
                                  }}
                                >
                                  Verify
                                </span>
                              </label>
                              <div className="user-about medium">{user.email}</div>
                            </div>
                          </div>
                        </div>

                        <div className="s4 col">
                          <div className="d-flex info-box">
                            <div className="info">
                              <label className="label">Mobile</label>
                              <div className="user-about medium">{user.mobile || "-"}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p>{withoutOnboard ? "No onboard data available" : "No user data available"}</p>
                    )}
                  </div>
                )}
              </UserInfoWrapper>
            )}
          </div>
        </div>
      </div>

      {/* Verify Email Modal */}
      <Dialog
        open={openVerifyModal}
        onClose={() => setOpenVerifyModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            padding: "25px",
            minHeight: "250px",
          },
        }}
      >
        <DialogTitle>Email Verification</DialogTitle>
        <DialogContent>
          <TextField
            label="Email"
            value={emailToVerify}
            onChange={(e) => setEmailToVerify(e.target.value)}
            fullWidth
            margin="dense"
  InputProps={{ disableUnderline: true }}
          />
          {otpSent && (
            <TextField
              label="Enter OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              fullWidth
              margin="dense"
              InputProps={{ disableUnderline: true }}
            />
          )}
          {otpMessage && (
            <p
              style={{
                color: otpMessage.includes("success") ? "green" : "red",
                marginTop: 10,
                fontWeight: 500,
              }}
            >
              {otpMessage}
            </p>
          )}
        </DialogContent>
        <DialogActions>
          {!otpSent ? (
            <Button color="primary" onClick={handleSendOtp} disabled={otpLoading}>
              {otpLoading ? "Sending..." : "Send OTP"}
            </Button>
          ) : (
            <Button color="primary" onClick={handleVerifyOtp} disabled={otpLoading}>
              {otpLoading ? "Verifying..." : "Verify OTP"}
            </Button>
          )}
          <Button onClick={() => setOpenVerifyModal(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </ResponsiveContentWrapper>
  );
};

export default Profile;
